import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { ClientPicker } from '../components/ClientPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewProduction() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const [title, setTitle] = useState('');
  const [shootDate, setShootDate] = useState('');
  const [callTime, setCallTime] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClientChange = (id, _name) => {
    setClientId(id);
    // name is optional and can be ignored in this component
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!clientId) {
      setError('Please select a client');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API}/productions`,
        {
          client_id: clientId,
          title,
          shoot_date: shootDate || undefined,
          call_time: callTime || undefined,
          location: location || undefined,
          status: 'planning',
          priority: 'medium',
        },
        { withCredentials: true }
      );
      navigate(`/clients/${clientId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create shoot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-foreground mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <h1 className="text-3xl font-serif font-bold text-foreground mb-6" data-testid="new-production-title">
          New Shoot
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Client *
            </label>
            <ClientPicker value={clientId} onChange={handleClientChange} error={error && !clientId ? error : ''} />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="Shoot title"
              data-testid="production-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Shoot Date
            </label>
            <input
              type="date"
              value={shootDate}
              onChange={(e) => setShootDate(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="production-date-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Call Time
            </label>
            <input
              type="time"
              value={callTime}
              onChange={(e) => setCallTime(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="production-calltime-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="Shoot location"
              data-testid="production-location-input"
            />
          </div>

          {error && !(!clientId) && (
            <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-secondary text-foreground hover:bg-secondary/80 border border-border rounded-lg px-4 py-2 font-medium transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-background hover:bg-primary/90 rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Creating...' : 'Create Shoot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
